import { supabase } from "./supabase";

interface CreateAdminCommand {
  action: "create";
  email: string;
  display_name: string;
  role: "owner" | "admin" | "editor";
  password: string;
}

interface DeleteAdminCommand {
  action: "delete";
  email: string;
}

interface ResetAdminPasswordCommand {
  action: "reset_password";
  email: string;
  password: string;
}

type AdminUserCommand = CreateAdminCommand | DeleteAdminCommand | ResetAdminPasswordCommand;

async function edgeErrorMessage(data: unknown, error: unknown): Promise<string> {
  const dataMessage = (data as { error?: string } | null)?.error;
  if (dataMessage) return dataMessage;
  const context = (error as { context?: unknown } | null)?.context;
  if (context instanceof Response) {
    const payload = await context.clone().json().catch(() => ({})) as { error?: string };
    if (payload.error) return payload.error;
  }
  return error instanceof Error ? error.message : "";
}

async function callNetlifyFallback(command: AdminUserCommand, accessToken: string): Promise<void> {
  const endpoint = command.action === "create" ? "create-admin" : command.action === "delete" ? "delete-admin" : "reset-admin-password";
  const response = await fetch(`/.netlify/functions/${endpoint}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify(command),
  });
  const result = await response.json().catch(() => ({})) as { error?: string };
  if (!response.ok) throw new Error(result.error || "Die serverseitige Benutzerverwaltung ist nicht erreichbar.");
}

export async function runAdminUserCommand(command: AdminUserCommand, accessToken: string): Promise<void> {
  const { data, error } = await supabase.functions.invoke("admin-users", { body: command });
  if (!error && !(data as { error?: string } | null)?.error) return;
  const edgeMessage = await edgeErrorMessage(data, error);

  try {
    await callNetlifyFallback(command, accessToken);
  } catch (fallbackError) {
    const fallbackMessage = fallbackError instanceof Error ? fallbackError.message : "Serverfunktion nicht erreichbar.";
    const edgeUnavailable = !edgeMessage || /not found|failed to fetch|function.*(missing|unavailable)/i.test(edgeMessage);
    throw new Error(edgeUnavailable ? fallbackMessage : edgeMessage);
  }
}
